// packages/web/src/components/service/BrandDisplay.tsx
// Reusable component for displaying brand/provider information
// FAANG-GRADE: Upgraded — larger logo, brand description, Aithon trust chip

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { Box, Card, CardContent, Chip, Collapse, Divider, IconButton, Link, Typography } from '@mui/material';
import React from 'react';

import { BrandLogo } from '../common/BrandLogo';

interface BrandDisplayProps {
  brandName: string;
  brandDescription?: string;
  brandLogoPath?: string;
  brandVideoUrl?: string;
  serviceVideoUrl?: string;
  brandWebsite?: string;
  installDays?: number;
}

const parseYtId = (url: string): string | null => {
  try {
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return ytMatch ? ytMatch[1] : null;
  } catch { return null; }
};

const parseVideoEmbedUrl = (url: string): string | null => {
  try {
    const ytId = parseYtId(url);
    if (ytId) return `https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1&enablejsapi=1`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    if (url.includes('youtube.com/c/') || url.includes('youtube.com/@')) return null;
    return null;
  } catch { return null; }
};

const BrandDisplay: React.FC<BrandDisplayProps> = ({
  brandName,
  brandDescription,
  brandLogoPath,
  brandVideoUrl,
  serviceVideoUrl,
  brandWebsite,
  installDays,
}) => {
  // Brand video only (service video is handled by ServiceVideoSection above)
  const brandEmbedUrl = brandVideoUrl ? parseVideoEmbedUrl(brandVideoUrl) : null;
  const brandYtId = brandVideoUrl ? parseYtId(brandVideoUrl) : null;
  const brandThumbUrl = brandYtId ? `https://img.youtube.com/vi/${brandYtId}/mqdefault.jpg` : null;
  const [brandExpanded, setBrandExpanded] = React.useState(false);
  const [brandVideoVisible, setBrandVideoVisible] = React.useState(true);

  React.useEffect(() => {
    if (!brandEmbedUrl || !brandExpanded) return;
    const handler = (e: MessageEvent) => {
      try {
        const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        if (data?.event === 'onError' && [100, 101, 150].includes(Number(data?.info))) {
          setBrandVideoVisible(false);
        }
      } catch { /* ignore */ }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [brandEmbedUrl, brandExpanded]);

  return (
    <Card variant="outlined" sx={{ borderColor: '#e2e8f0' }}>
      <CardContent sx={{ p: 3 }}>
        {/* Brand row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <BrandLogo
            src={brandLogoPath}
            alt={`${brandName} logo`}
            size={56}
            lazy={true}
            prefetch={false}
          />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {brandName}
              </Typography>
              {brandWebsite && (
                <Link
                  href={brandWebsite}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  underline="hover"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                  Website <OpenInNewIcon sx={{ fontSize: 14 }} />
                </Link>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Service Provider
            </Typography>
          </Box>
        </Box>

        {brandDescription && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
            {brandDescription}
          </Typography>
        )}

        {brandEmbedUrl && brandVideoVisible && brandThumbUrl && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
              About {brandName}
            </Typography>
            {!brandExpanded ? (
              /* Thumbnail row */
              <Box
                onClick={() => setBrandExpanded(true)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
                  borderRadius: 1, overflow: 'hidden', border: '1px solid #e2e8f0',
                  '&:hover': { borderColor: '#94a3b8', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' },
                  transition: 'all 0.15s',
                  maxWidth: 320,
                }}
              >
                <Box sx={{ position: 'relative', flexShrink: 0, width: 120, height: 68, overflow: 'hidden' }}>
                  <Box
                    component="img"
                    src={brandThumbUrl}
                    alt={`${brandName} video thumbnail`}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={() => setBrandVideoVisible(false)}
                  />
                  <Box sx={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(0,0,0,0.25)',
                  }}>
                    <PlayCircleOutlineIcon sx={{ color: 'white', fontSize: 28, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }} />
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ pr: 1, lineHeight: 1.4 }}>
                  Watch company overview ▶
                </Typography>
              </Box>
            ) : (
              /* Expanded player */
              <Box>
                <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: 1, overflow: 'hidden', mb: 0.5 }}>
                  <iframe
                    src={brandEmbedUrl}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={`${brandName} company video`}
                    loading="lazy"
                    onError={() => setBrandVideoVisible(false)}
                  />
                </Box>
                <Typography
                  variant="caption" color="text.secondary"
                  onClick={() => setBrandExpanded(false)}
                  sx={{ cursor: 'pointer', '&:hover': { color: 'text.primary' } }}
                >
                  ▲ collapse
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {installDays && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Chip
              label={`📅 Typical delivery: ${installDays} business days`}
              size="small"
              sx={{
                backgroundColor: '#eff6ff',
                color: '#1d4ed8',
                border: '1px solid #bfdbfe',
                fontWeight: 500,
                fontSize: '0.7rem',
              }}
            />
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Aithon guarantee badge */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label="🛡️ Perk delivery guaranteed by Aithon"
            size="small"
            sx={{
              backgroundColor: '#eff6ff',
              color: '#1d4ed8',
              border: '1px solid #bfdbfe',
              fontWeight: 500,
              fontSize: '0.7rem',
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default React.memo(BrandDisplay);
